import React, { useCallback, useEffect, useState } from "react";
import SingleMarkerMap from "../../common/components/maps/SingleMarkerMap";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
  Grid,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  IconButton,
  Tooltip,
  Link,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { toast } from "react-toastify";
import MDButton from "components/MDButton";
import pointsService from "services/pointsService";
import orgDataService from "services/orgDataService";
import { clientPointToHub, PointResult } from "points/models";
import { usePointPermissions } from "points/permissions";
import {
  hubDeactivationWarning,
  hubStateNote,
  isHubEffectivelyActive,
  pointDeactivationWarning,
  pointToggleGuard,
} from "points/pointActivation";
import { ACTION_BUTTON_SX } from "shifts/shiftStyles";
import { useParams } from "react-router-dom";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";

/**
 * A deactivation waiting to be confirmed.
 *
 * Only deactivations sit here. Turning something back on is not a change anyone has to be talked
 * out of, and making it a two-step would just be friction on the fix this screen exists to offer.
 */
type PendingDeactivation = { kind: "point" } | { kind: "hub"; hub: clientPointToHub };

const Points = () => {
  let { id } = useParams();
  const { canAddPointHub, canCreatePoint } = usePointPermissions();
  const [point, setPoint] = useState<PointResult>();
  const [isLoading, setIsLoading] = useState(true);

  // Add-hub dialog. The picker is scoped to this point's client, which is the pairing that
  // makes sense operationally -- a point belongs to a client, and its hubs serve that client.
  const [isHubDialogOpen, setIsHubDialogOpen] = useState(false);
  const [clientHubs, setClientHubs] = useState<any[]>([]);
  const [selectedHubId, setSelectedHubId] = useState<number | "">("");
  const [isAddingHub, setIsAddingHub] = useState(false);

  // Activation state. Tracked per hub link rather than per screen: one row failing says nothing
  // about the next, and the grid stays readable while one switch is in flight.
  const [togglingHubId, setTogglingHubId] = useState<number | null>(null);
  const [isTogglingPoint, setIsTogglingPoint] = useState(false);
  const [pendingDeactivation, setPendingDeactivation] = useState<PendingDeactivation | null>(null);

  const loadPoint = useCallback(() => {
    if (!id) return;
    setIsLoading(true);
    pointsService
      .getPoint(+id)
      .then((result) => {
        if (!result) return;
        setPoint(result);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  useEffect(() => {
    loadPoint();
  }, [loadPoint]);

  const openHubDialog = () => {
    if (!point?.clientId) {
      toast.error("This location has no client, so its hubs cannot be listed.");
      return;
    }

    setSelectedHubId("");
    setIsHubDialogOpen(true);

    orgDataService
      .getHubsByClient(point.clientId)
      .then((hubs) => setClientHubs(hubs ?? []))
      .catch(() => toast.error("Could not load hubs for this client."));
  };

  const addHub = async () => {
    if (!id || selectedHubId === "") return;

    setIsAddingHub(true);
    try {
      const result = await pointsService.addHub(+id, Number(selectedHubId));
      if (!result?.success) {
        toast.error(result?.message || "Could not add the hub.");
        return;
      }

      toast.success(result.message);
      setIsHubDialogOpen(false);
      loadPoint();
    } catch {
      toast.error("Could not add the hub.");
    } finally {
      setIsAddingHub(false);
    }
  };

  /**
   * Turns one point-of-service link on or off.
   *
   * The link is addressed by its OWN key, not by the PointOfServiceID the grid shows -- two
   * separate columns, and toggling by the wrong one would flip an unrelated row. Anything without
   * that key is left un-togglable rather than guessed at.
   *
   * The row is updated from the server's answer rather than from what was asked for, so the grid
   * shows what actually happened. No client restriction: hub links are Railcrew's own routing
   * data, and the case this was built for is a UP point.
   */
  const setHubActive = async (hub: clientPointToHub, isActive: boolean) => {
    if (!id || hub.clientPointToHubId === undefined || hub.clientPointToHubId === null) return;

    if (typeof point?.clientId !== "number") {
      toast.error("This location has no client on it, so its hubs cannot be changed.");
      return;
    }

    setTogglingHubId(hub.clientPointToHubId);
    const outcome = await pointsService.setPointHubActive(+id, hub.clientPointToHubId, point.clientId, isActive);
    setTogglingHubId(null);

    if (outcome.status === "error") {
      // OrderApi's own message. It is the only thing that says why -- the link is not on this
      // point, or the row is gone -- and "could not update" would not.
      toast.error(outcome.message);
      return;
    }

    // The point's own flag is taken from the answer too. It is the other half of what
    // geo.PointOfService reports, and reading it back keeps the grid honest if the point was
    // deactivated somewhere else while this screen was open.
    setPoint((previous) =>
      previous
        ? {
            ...previous,
            isActive: outcome.hub.isClientPointActive,
            clientPointToHubs: previous.clientPointToHubs?.map((row) =>
              row.clientPointToHubId === outcome.hub.clientPointToHubId
                ? { ...row, isActive: outcome.hub.isActive }
                : row
            ),
          }
        : previous
    );

    // Switching a link on under a deactivated point changes nothing anyone can use. Saying so at
    // the moment it happens is the point of the whole feature -- a plain success toast here would
    // be the same lie the screen used to tell.
    const note = hubStateNote(outcome.hub.isActive, outcome.hub.isClientPointActive);
    if (note) {
      toast.warning(note);
      return;
    }

    toast.success(
      outcome.hub.message || (outcome.hub.isActive ? "Point of service activated." : "Point of service deactivated.")
    );
  };

  /**
   * Activates or deactivates the point itself.
   *
   * Client-scoped, and refused by OrderApi for the railroads that send their own locations. The
   * screen keeps its control read-only for those rather than letting a user produce a refusal,
   * but the server is the gate.
   *
   * Reloads on success rather than patching the flag in place: deactivating a point is not only a
   * flag, and the hub grid underneath is read from the same payload.
   */
  const setPointActive = async (isActive: boolean) => {
    if (!id || typeof point?.clientId !== "number") return;

    setIsTogglingPoint(true);
    const outcome = isActive
      ? await pointsService.activatePoint(+id, point.clientId)
      : await pointsService.deactivatePoint(+id, point.clientId);
    setIsTogglingPoint(false);

    if (outcome.status === "error" || outcome.status === "invalid") {
      toast.error(outcome.message);
      return;
    }

    toast.success(outcome.point.message || (isActive ? "Location activated." : "Location deactivated."));
    loadPoint();
  };

  const confirmDeactivation = async () => {
    if (!pendingDeactivation) return;

    if (pendingDeactivation.kind === "hub") {
      await setHubActive(pendingDeactivation.hub, false);
    } else {
      await setPointActive(false);
    }

    setPendingDeactivation(null);
  };

  const columns: GridColDef[] = [
    {
      field: "pointOfServiceID",
      headerName: "POS Id",
      flex: 1,
    },
    { field: "location", headerName: "Hub", flex: 1 },
    { field: "distance", headerName: "Distance", flex: 1 },
    { field: "driveTime", headerName: "Drive Time", flex: 1 },
    {
      field: "lastUpdateDate",
      headerName: "Last Updated",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any, number>) =>
        new Date(params.row.lastUpdateDate).toLocaleDateString(),
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 165,
      sortable: false,
      // Actionable rather than a printed flag. A point can be live while the link that makes it
      // selectable from a hub is not, which reads everywhere else as the location simply not
      // being there -- and until now there was nothing on any screen that could put it right.
      //
      // Two things in one cell, because there are two different facts and conflating them is the
      // bug: the SWITCH is the link's own flag, which is the only thing this toggle writes, and
      // the CHIP is what the point of service actually does. geo.PointOfService.IsActive is the
      // two ANDed, so a link switched on under a deactivated point still works nowhere. Showing
      // the switch alone would rebuild the "it says it is active but it does not work" complaint.
      renderCell: (params: GridRenderCellParams<any, boolean>) => {
        const hub = params.row as clientPointToHub;
        const hasId = hub.clientPointToHubId !== undefined && hub.clientPointToHubId !== null;
        const isBusy = togglingHubId === hub.clientPointToHubId;
        const isEffective = isHubEffectivelyActive(hub.isActive, point?.isActive);
        const blockedNote = hubStateNote(hub.isActive, point?.isActive);

        let title: string;
        if (!canAddPointHub) {
          title = "You do not have permission to change hub links.";
        } else if (!hasId) {
          title = "This link has no id to act on, so it cannot be changed here.";
        } else if (hub.isActive) {
          title = "Switch off to stop offering this location from this hub.";
        } else {
          title = "Switch on to make this location selectable from this hub again.";
        }

        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={title}>
              {/* A disabled control fires no events, so the tooltip needs a wrapper that does. */}
              <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                {isBusy ? (
                  <CircularProgress size={14} sx={{ mx: 1.25 }} />
                ) : (
                  <Switch
                    size="small"
                    checked={!!hub.isActive}
                    disabled={!canAddPointHub || !hasId || togglingHubId !== null}
                    onChange={(_, checked) =>
                      checked ? setHubActive(hub, true) : setPendingDeactivation({ kind: "hub", hub })
                    }
                    inputProps={{ "aria-label": `${hub.location || "Hub"} active` }}
                  />
                )}
              </Box>
            </Tooltip>

            {blockedNote ? (
              <Tooltip title={blockedNote}>
                <Chip size="small" color="warning" variant="outlined" label="Blocked" />
              </Tooltip>
            ) : (
              <Chip
                size="small"
                color={isEffective ? "success" : "default"}
                variant="outlined"
                label={isEffective ? "Active" : "Inactive"}
              />
            )}
          </Stack>
        );
      },
    },
  ];

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(10)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" width="98%" sx={{ my: 3, mx: 1, overflow: "hidden" }} />
      ))}
    </Box>
  );

  const hubs = point?.clientPointToHubs ?? [];
  const toggleGuard = pointToggleGuard(point?.clientId);
  const isTogglingSomething = isTogglingPoint || togglingHubId !== null;

  // Links switched on under a deactivated point. Their own flags say active, geo.PointOfService
  // says otherwise, and the fix is on the point rather than on any of these rows -- so it is said
  // once, in the open, rather than left to a tooltip on each chip.
  const blockedHubCount = point?.isActive === false ? hubs.filter((hub) => hub.isActive).length : 0;

  const confirmationWarning = !pendingDeactivation
    ? ""
    : pendingDeactivation.kind === "hub"
    ? hubDeactivationWarning(pendingDeactivation.hub.location)
    : pointDeactivationWarning(point?.name, hubs.length);

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography gutterBottom sx={{ color: "text.secondary", fontSize: 14 }}>
                Client Point Info
              </Typography>
              <Grid container>
                <Grid item xs={6}>
                  <b>Point Name: </b>
                </Grid>
                <Grid item xs={6} >
                  {point?.name || "--"}
                </Grid>
                <Grid item xs={6}>
                  <b>Point Name Alt: </b>
                </Grid>
                <Grid item xs={6}>
                  {point?.nameAlt || "--"}
                </Grid>
                <Grid item xs={6}>
                  <b>Client: </b>
                </Grid>
                <Grid item xs={6}>
                  {point?.clientName || "--"}
                </Grid>
                <Grid item xs={6}>
                  <b>Address: </b>
                </Grid>
                <Grid item xs={6}>
                  {point?.address || "--"}
                </Grid>
                <Grid item xs={6}>
                  <b>City: </b>
                </Grid>
                <Grid item xs={6}>
                  {point?.city || "--"}
                </Grid>
                <Grid item xs={6}>
                  <b>State: </b>
                </Grid>
                <Grid item xs={6}>
                  {point?.state || "--"}
                </Grid>
                <Grid item xs={6}>
                  <b>Zip: </b>
                </Grid>
                <Grid item xs={6}>
                  {point?.zip || "--"}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <b>Latitude: </b>
                </Grid>
                <Grid item xs={6}>
                  {point?.latitude || "--"}
                </Grid>
                <Grid item xs={6}>
                  <b>Longitude: </b>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {point?.longitude || "--"}
                </Grid>

                {point?.latitude && point?.longitude && (
                  <>
                    <Grid item xs={6}>
                      <b>Map Link: </b>
                    </Grid>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
                        <Link
                          href={`https://www.google.com/maps?q=${point.latitude},${point.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
                        >
                          Open in Google Maps
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                        <Tooltip title="Copy map link">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const url = `https://www.google.com/maps?q=${point.latitude},${point.longitude}`;
                              navigator.clipboard.writeText(url)
                                .then(() => toast.success("Map link copied"))
                                .catch(() => toast.error("Failed to copy"));
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>
                  </>
                )}

                <Grid item xs={6}>
                  <b>Is Active: </b>
                </Grid>
                <Grid item xs={6}>
                  {point?.isActive ? "True" : "False"}
                </Grid>
                <Grid item xs={6}>
                  <b>Created Date: </b>
                </Grid>
                <Grid item xs={6}>
                  {point?.createdDate ? new Date(point?.createdDate).toLocaleDateString() : "--"}
                </Grid>

                {/* Retiring or reviving a location is a dispatch-visible change, so it sits under
                    the facts rather than beside the flag, and it says what it will do before it
                    does it.

                    Read-only for the railroads that send their own locations: the state above
                    still reads, the control does not operate, and the reason sits next to it.
                    OrderApi refuses those clients too -- this only stops the screen offering an
                    action that can never succeed. */}
                {canCreatePoint && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1.5 }}>
                      <MDButton
                        variant="outlined"
                        color={point?.isActive ? "error" : "success"}
                        size="small"
                        disabled={!toggleGuard.allowed || isTogglingSomething || isLoading}
                        onClick={() =>
                          point?.isActive ? setPendingDeactivation({ kind: "point" }) : setPointActive(true)
                        }
                        startIcon={isTogglingPoint ? <CircularProgress size={12} color="inherit" /> : undefined}
                      >
                        {point?.isActive ? "Deactivate location" : "Activate location"}
                      </MDButton>

                      {!toggleGuard.allowed && (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
                          {toggleGuard.reason}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <SingleMarkerMap location={point} height="260px"/>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography gutterBottom sx={{ color: "text.secondary", fontSize: 14 }}>
                  Client Point Hubs
                </Typography>
                <Box sx={{ flex: 1 }} />
                {/* A point is only selectable from a hub once this link exists, so adding one
                    is self-service for anyone holding AddPointHub. Removing stays with an
                    administrator. */}
                {canAddPointHub && (
                  <Tooltip title="Assign a hub to this location so it can be selected from that hub.">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      sx={ACTION_BUTTON_SX}
                      onClick={openHubDialog}
                      disabled={isLoading}
                    >
                      Add Hub
                    </Button>
                  </Tooltip>
                )}
              </Stack>
              {blockedHubCount > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {`${blockedHubCount} hub ${blockedHubCount === 1 ? "link is" : "links are"} switched on, but this ` +
                    "location is deactivated, so none of them can be used. Activate the location to bring them back."}
                </Alert>
              )}
              <Grid container spacing={1} sx={{ fontSize: 11 }}>
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, overflow: "hidden" }}>
                    <Paper sx={{ width: "99%", height: "auto", overflow: "hidden" }}>
                      <DataGrid
                        sx={{ overflow: "hidden", height: isLoading ? "100vh" : "auto", fontSize: ".85rem" }}
                        rows={point?.clientPointToHubs || []}
                        getRowId={(row) => row.pointOfServiceID}
                        columns={columns}
                        disableColumnMenu
                        slots={{
                          loadingOverlay: LoadingSkeleton,
                        }}
                        loading={isLoading}
                        density="compact"
                        getRowClassName={(params) =>
                          params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
                        }
                      />
                    </Paper>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={isHubDialogOpen} onClose={() => setIsHubDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Hub</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {point?.name}
            {point?.clientName ? ` · ${point.clientName}` : ""}
          </Typography>

          <FormControl size="small" fullWidth sx={{ mt: 1 }}>
            <InputLabel id="add-hub-label">Hub</InputLabel>
            <Select
              labelId="add-hub-label"
              label="Hub"
              value={selectedHubId}
              onChange={(e) => setSelectedHubId(e.target.value as number)}
            >
              {clientHubs.map((hub) => (
                <MenuItem key={hub.id ?? hub.hubId} value={hub.id ?? hub.hubId}>
                  {hub.location ?? hub.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Distance is left at the server's placeholder; the geo backfill replaces it. */}
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 2 }}>
            Distance and drive time are filled in later by the distance backfill.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsHubDialogOpen(false)} disabled={isAddingHub}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={ACTION_BUTTON_SX}
            onClick={addHub}
            disabled={selectedHubId === "" || isAddingHub}
          >
            {isAddingHub ? "Adding…" : "Add Hub"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivations only. Turning something back on is not a decision anyone needs talking out
          of, and a second step there would be friction on the exact fix this screen is for. The
          copy names consequences rather than asking about a flag -- "are you sure?" tells a
          dispatcher nothing about what stops working. */}
      <Dialog
        open={pendingDeactivation !== null}
        onClose={() => !isTogglingSomething && setPendingDeactivation(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {pendingDeactivation?.kind === "hub" ? "Deactivate this point of service?" : "Deactivate this location?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {confirmationWarning}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setPendingDeactivation(null)}
            disabled={isTogglingSomething}
          >
            Cancel
          </MDButton>
          <MDButton variant="contained" color="error" onClick={confirmDeactivation} disabled={isTogglingSomething}>
            {isTogglingSomething ? "Deactivating…" : "Deactivate"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Points;
